import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import {
  type RolePermissionValue,
  type SettingRole,
  type SettingRolePayload,
} from '../../store/api/settingsApi'
import {
  HIDE_ON_MOBILE_INLINE,
  STACK_RESPONSIVE_DIRECTION_PROPS,
  TEXT_TRUNCATE,
} from '../../theme/responsive'

type PermissionKey =
  | 'purchase'
  | 'procurement'
  | 'saleContract'
  | 'saleControl'
  | 'factory'
  | 'virtualWarehouse'
  | 'futures'
  | 'position'
  | 'quality'
  | 'masterData'
  | 'setting'
  | 'factoryBl'
  | 'factoryNc'
  | 'factoryNs'
  | 'factoryNt'
  | 'factoryPaco'
  | 'matchingPs'

interface RoleFormState {
  roleCodeUper: string
  roleName: string
  description: string
  purchase: RolePermissionValue
  procurement: RolePermissionValue
  saleContract: RolePermissionValue
  saleControl: RolePermissionValue
  factory: RolePermissionValue
  virtualWarehouse: RolePermissionValue
  futures: RolePermissionValue
  position: RolePermissionValue
  quality: RolePermissionValue
  masterData: RolePermissionValue
  setting: RolePermissionValue
  factoryBl: RolePermissionValue
  factoryNc: RolePermissionValue
  factoryNs: RolePermissionValue
  factoryNt: RolePermissionValue
  factoryPaco: RolePermissionValue
  matchingPs: RolePermissionValue
}

interface Props {
  open: boolean
  mode: 'new' | 'edit'
  initialRole?: SettingRole | null
  onClose: () => void
  onSubmit: (payload: SettingRolePayload) => Promise<void>
  isSubmitting?: boolean
}

const headerButtonSx = {
  minHeight: 28,
  px: 1,
  py: 0.25,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none',
} as const

const PERMISSION_OPTIONS: Array<{ value: RolePermissionValue; label: string }> = [
  { value: 0, label: 'View' },
  { value: 1, label: 'Edit' },
  { value: 2, label: 'No access' },
]

const PERMISSION_ROWS: Array<{ key: PermissionKey; label: string }> = [
  { key: 'purchase', label: 'Purchase' },
  { key: 'procurement', label: 'Procurement' },
  { key: 'saleContract', label: 'Sales Contract' },
  { key: 'saleControl', label: 'Sale Control' },
  { key: 'setting', label: 'Setting' },
  { key: 'virtualWarehouse', label: 'Virtual Warehouse' },
  { key: 'futures', label: 'Futures' },
  { key: 'position', label: 'Report' },
  { key: 'quality', label: 'Quality' },
  { key: 'masterData', label: 'Master Data' },
  { key: 'factory', label: 'Factory control' },
  { key: 'factoryBl', label: 'Bảo Lộc factory' },
  { key: 'factoryNc', label: 'Nguyên Cường factory' },
  { key: 'factoryNs', label: 'Nông Sản factory' },
  { key: 'factoryNt', label: 'Nhơn Trạch factory' },
  { key: 'factoryPaco', label: 'Pacorini factory' },
  { key: 'matchingPs', label: 'Matching P-S' },
]

const emptyForm = (): RoleFormState => ({
  roleCodeUper: '',
  roleName: '',
  description: '',
  purchase: 2,
  procurement: 2,
  saleContract: 2,
  saleControl: 2,
  factory: 2,
  virtualWarehouse: 2,
  futures: 2,
  position: 2,
  quality: 2,
  masterData: 2,
  setting: 2,
  factoryBl: 2,
  factoryNc: 2,
  factoryNs: 2,
  factoryNt: 2,
  factoryPaco: 2,
  matchingPs: 2,
})

const fromRole = (role?: SettingRole | null): RoleFormState => {
  if (!role) return emptyForm()
  return {
    roleCodeUper: role.roleCodeUper ?? '',
    roleName: role.roleName ?? '',
    description: role.description ?? '',
    purchase: role.purchase ?? 2,
    procurement: role.procurement ?? 2,
    saleContract: role.saleContract ?? 2,
    saleControl: role.saleControl ?? 2,
    factory: role.factory ?? 2,
    virtualWarehouse: role.virtualWarehouse ?? 2,
    futures: role.futures ?? 2,
    position: role.position ?? 2,
    quality: role.quality ?? 2,
    masterData: role.masterData ?? 2,
    setting: role.setting ?? 2,
    factoryBl: role.factoryBl ?? 2,
    factoryNc: role.factoryNc ?? 2,
    factoryNs: role.factoryNs ?? 2,
    factoryNt: role.factoryNt ?? 2,
    factoryPaco: role.factoryPaco ?? 2,
    matchingPs: role.matchingPs ?? 2,
  }
}

const extractApiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object') return 'Failed to save role. Please try again.'
  if (!('data' in error)) return 'Failed to save role. Please try again.'

  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return 'Failed to save role. Please try again.'

  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (typeof first === 'string') return first
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg
      if (typeof msg === 'string' && msg.trim()) return msg
    }
    return JSON.stringify(first)
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail)

  return 'Failed to save role. Please try again.'
}

export default function SettingsRoleDialog({ open, mode, initialRole, onClose, onSubmit, isSubmitting = false }: Props) {
  const [form, setForm] = useState<RoleFormState>(emptyForm)
  const [validationError, setValidationError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setForm(fromRole(mode === 'edit' ? initialRole ?? null : null))
    setValidationError(null)
  }, [open, mode, initialRole])

  const title = useMemo(() => (mode === 'new' ? 'Role - Add / Edit' : 'Role - Add / Edit'), [mode])

  const handleClose = () => {
    setForm(emptyForm())
    setValidationError(null)
    onClose()
  }

  const handlePermissionChange = (key: PermissionKey, value: string) => {
    const nextValue = Number(value) as RolePermissionValue
    setForm((prev) => ({
      ...prev,
      [key]: nextValue,
    }))
  }

  const handleSave = async () => {
    const roleCodeUper = form.roleCodeUper.trim().toUpperCase()
    const roleName = form.roleName.trim()
    const description = form.description.trim()

    if (!roleCodeUper) {
      setValidationError('Please type Role Code!')
      return
    }
    if (!roleName) {
      setValidationError('Please type Role Name!')
      return
    }

    setValidationError(null)
    try {
      await onSubmit({
        roleCodeUper,
        roleName,
        description: description ? description : null,
        purchase: form.purchase,
        procurement: form.procurement,
        saleContract: form.saleContract,
        saleControl: form.saleControl,
        factory: form.factory,
        virtualWarehouse: form.virtualWarehouse,
        futures: form.futures,
        position: form.position,
        quality: form.quality,
        masterData: form.masterData,
        setting: form.setting,
        factoryBl: form.factoryBl,
        factoryNc: form.factoryNc,
        factoryNs: form.factoryNs,
        factoryNt: form.factoryNt,
        factoryPaco: form.factoryPaco,
        matchingPs: form.matchingPs,
      })
      handleClose()
    } catch (error: unknown) {
      setValidationError(extractApiErrorMessage(error))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(820px, 90vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          flexWrap: 'nowrap',
        }}
      >
        <Typography
          component="span"
          noWrap
          title={title}
          sx={{
            ...TEXT_TRUNCATE,
            flex: '1 1 auto',
            fontWeight: 700,
            fontSize: '1rem',
            minWidth: 0,
          }}
        >
          {/* Mobile: drop module prefix "Role - " để compact title bar. */}
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            Role -{' '}
          </Box>
          Add / Edit
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button variant="contained" size="small" color="success" onClick={handleSave} disabled={isSubmitting} sx={headerButtonSx}>
            {isSubmitting ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button variant="outlined" size="small" onClick={handleClose} disabled={isSubmitting} sx={headerButtonSx}>
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          pt: 0.75,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.81rem' },
          '& .MuiInputBase-input': { py: 0.5625, fontSize: '0.81rem' },
          '& .MuiInputLabel-root': { fontSize: '0.81rem' },
          '& .MuiTypography-body2': { fontSize: '0.81rem' },
          '& .MuiFormControlLabel-label': { fontSize: '0.79rem' },
        }}
      >
        {validationError ? (
          <Alert severity="error" sx={{ mb: 1, fontSize: '0.81rem' }}>
            {validationError}
          </Alert>
        ) : null}

        <Box sx={{ pb: 1 }}>
          <Grid container spacing={0.625}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Role Code"
                size="small"
                fullWidth
                autoFocus={mode === 'new'}
                value={form.roleCodeUper}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    roleCodeUper: e.target.value.toUpperCase(),
                  }))
                }
                disabled={mode === 'edit'}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                label="Role Name"
                size="small"
                fullWidth
                value={form.roleName}
                onChange={(e) => setForm((prev) => ({ ...prev, roleName: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 0.25 }} />
            </Grid>

            {PERMISSION_ROWS.map((row) => (
              <Grid item xs={12} sm={6} key={row.key}>
                <Stack
                  {...STACK_RESPONSIVE_DIRECTION_PROPS}
                  spacing={{ xs: 0.25, sm: 1 }}
                  sx={{
                    px: 1,
                    py: 0.375,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      minWidth: { xs: 'auto', sm: 150 },
                      fontWeight: 600,
                      fontSize: '0.79rem',
                    }}
                  >
                    {row.label}
                  </Typography>
                  <RadioGroup
                    row
                    value={String(form[row.key])}
                    onChange={(event) => handlePermissionChange(row.key, event.target.value)}
                    sx={{ flexWrap: 'wrap', columnGap: 0.75 }}
                  >
                    {PERMISSION_OPTIONS.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={String(option.value)}
                        control={<Radio size="small" />}
                        label={option.label}
                        sx={{ mr: 1 }}
                      />
                    ))}
                  </RadioGroup>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

